import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Microscope24Filled, Save20Regular, Send20Regular } from "@fluentui/react-icons";
import { Spinner } from "@fluentui/react-components";
import { FileReference } from "../OiReport";
import PowderMilling from "./PowderMilling";
import PowderRawMaterialCalculation from "./PowderRawMaterialCalculation";
import OiPowderTags from "./OiPowderTags";
import OiPowderPressing from "./OiPowderPressing";
import OiPowderSintering from "./OiPowderSintering";
import OiPowderComment from "./OiPowderComment";
import OiPageHeader, { OiHeaderButton } from "../OiPageHeader";
import { usePowder } from "../redux/ReduxContext";
import OiPowderStaff from "./OiPowderStaff";
import OiPowderFiles from "./OiPowderFiles";
import { email } from "../../../App";
import { CreateOiPowderOrder, GetOiPowderOrder, UpdateOiPowderOrder } from "../../../services/PowderOrderService";
import { PowderOrder } from "../redux/powderOrder";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { fadeIn } from "../EventReport/OiForm";
import PowderInfo from "./PowderInfo";
import PowderRawMaterialComposition from "./PowderRawMaterialComposition";


interface OiPowderFormProps {
    onOpenFileGallery?: (files: FileReference[], initialIndex: number) => void;
}

export default function OiPowderForm({ onOpenFileGallery }: OiPowderFormProps) {
    const hasLoaded = useRef(false);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);

    const { state, dispatch } = usePowder();
    const [orderId, setOrderId] = useState<string>("");
    const [createdAt, setCreatedAt] = useState<string>("");
    const [updatedAt, setUpdatedAt] = useState<string>("");
    const navigate = useNavigate();
    const { uuid } = useParams<{ uuid: string }>();
    const location = useLocation();
    const { key } = location;
    const [mode, setMode] = useState<"Create" | "Edit" | "Clone">("Create");
    const [isInitializing, setIsInitializing] = useState(true);

    const hydrateForm = useCallback(async (nextUuid: string, cancelled = false) => {
        try {
            const report = await GetOiPowderOrder(nextUuid);

            if (!report || cancelled) {
                return;
            }

            const data: PowderOrder = JSON.parse(report.jsonData);
            setOrderId(report?.orderId || "some error");
            setCreatedAt(report?.createdAt || "");
            setUpdatedAt(report?.lastUpdated || "");
            console.log("Hydrating form with data:", report?.orderId);
            dispatch({ type: 'HYDRATE', payload: data });
            dispatch({ type: 'SET_FIELD', payload: { field: 'orderId', value: report?.orderId } });
        } catch (error) {
            console.error("Error fetching powder order:", error);
        } finally {
            if (!cancelled) {
                setIsInitializing(false);
            }
        }
    }, [dispatch]);

    useLayoutEffect(() => {
        let cancelled = false;

        const initializeForm = async () => {
            setIsInitializing(true);

            if (location.pathname === `/Oi/powderOrderEdit/${uuid}`) {
                setMode("Edit");
                setOrderId("");

                if (uuid) {
                    await hydrateForm(uuid, cancelled);
                    return;
                }
            }
            else if (location.pathname === `/Oi/powderOrderClone/${uuid}`) {
                setMode("Clone");
                setOrderId("");

                if (uuid) {
                    await hydrateForm(uuid, cancelled);
                    return;
                }
            }
            else if (location.pathname === `/Oi/powderOrder/new`) {
                setMode("Create");
                setOrderId("");
                dispatch({
                    type: 'CLEAR_STATE',
                    payload: { author: email || "" }
                });
            }

            if (!cancelled) {
                setIsInitializing(false);
            }
        };

        initializeForm();

        return () => {
            cancelled = true;
        };
    }, [dispatch, hydrateForm, key, location.pathname, uuid]);


    const autoSave = useCallback(async () => {
        if (mode === "Edit") {
            const payload = JSON.stringify(state);
            if (uuid) {
                await UpdateOiPowderOrder(uuid, payload, true);
            }
        }
        else if (mode === "Create" || mode === "Clone") {
            const stateToSave = { ...state };
            stateToSave.author = email;
            const payload = JSON.stringify(stateToSave);

            const response = await CreateOiPowderOrder(payload, true, email);
            const parsed = JSON.parse(response || "{}");
            if (parsed.uuid) {
                navigate(`/Oi/powderOrderEdit/${parsed.uuid}`);
            }
        }
    }, [mode, navigate, state, uuid]);




    useEffect(() => {
        if (!hasLoaded.current) {
            hasLoaded.current = true;
            return;
        }

        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        timeoutRef.current = setTimeout(() => {

            autoSave();
            console.log("Auto-saving powder order...", state);

        }, 1000);

        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };

    }, [autoSave, state]);

    const save = async (isDraft: boolean) => {
        if (mode === "Create" || mode === "Clone") {
            let payload = JSON.stringify(state);
            const response = await CreateOiPowderOrder(payload, isDraft, email);
            const parsed = JSON.parse(response || "{}");
            navigate(`/Oi/powderOrder/${parsed.uuid}`);
        }
        else if (mode === "Edit") {
            const payload = JSON.stringify(state);
            if (uuid) {
                await UpdateOiPowderOrder(uuid, payload, isDraft);
                navigate(`/Oi/powderOrder/${uuid}`);
            }
        }
    }

    if (isInitializing) {
        return (
            <div className="h-screen flex items-center justify-center w-full max-w-none mx-6 pb-4" style={{ animation: "fadeIn 1s forwards" }}>
                <style>{fadeIn}</style>
                <Spinner />
            </div>
        );
    }

    return (
        <div className="w-full max-w-none mx-6 pb-4 text-primary-dark dark:text-primary-light" style={{ animation: "fadeIn 1s forwards" }}>
            <style>{fadeIn}</style>
            <OiPageHeader
                icon={<Microscope24Filled />}
                title={mode === "Edit" ? "Edit powder order" : mode === "Clone" ? "Clone powder order" : "New powder order"}
                id={mode === "Create" ? undefined : orderId}
                createdAt={mode === "Edit" ? createdAt : undefined}
                updatedAt={mode === "Edit" ? updatedAt : undefined}
                actions={<>
                    {mode === "Edit" && <OiHeaderButton variant="secondary" onClick={() => navigate(`/Oi/powderOrder/${uuid}`)}>Back</OiHeaderButton>}
                    <OiHeaderButton variant="secondary" icon={<Save20Regular />} onClick={() => save(true)}>Save draft</OiHeaderButton>
                    <OiHeaderButton icon={<Send20Regular />} onClick={() => save(false)}>Submit</OiHeaderButton>
                </>}
            />

            <div className="grid w-full xl:gap-4 md:grid-cols-1 xl:grid-cols-2">
                <PowderInfo />
                <OiPowderComment />

            </div>
            <PowderMilling />
            <PowderRawMaterialCalculation />
            <PowderRawMaterialComposition />

            <div className="grid w-full grid-cols-1 gap-4 2xl:grid-cols-2 2xl:items-start">
                <div>
                    <OiPowderPressing />
                    <OiPowderSintering />

                </div>

                <div>
                    <OiPowderTags />
                    <OiPowderStaff />
                    <OiPowderFiles onOpenFileGallery={onOpenFileGallery} />

                </div>

            </div>


        </div>
    );
};
